import { LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth, useCan } from '../features/auth/AuthProvider';

function FullPageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-label="Loading">
      <LoaderCircle aria-hidden className="size-6 animate-spin text-fg-subtle" />
    </div>
  );
}

/**
 * UI routing guard only (the API is the security boundary). Sends anonymous users to the right
 * sign-in page and keeps tenant users out of the platform console and vice versa.
 */
export function RequireAuth({ type, children }: { type: 'tenant' | 'platform'; children: ReactNode }) {
  const { status, me } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous' || !me) {
    return (
      <Navigate
        to={type === 'platform' ? '/platform/login' : '/login'}
        replace
        state={{ from: location.pathname }}
      />
    );
  }
  if (me.user.type !== type)
    return <Navigate to={me.user.type === 'platform' ? '/platform' : '/app'} replace />;
  return <>{children}</>;
}

/** Owners land in the setup wizard until onboarding is finished. */
export function OnboardingGate({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const can = useCan();
  const location = useLocation();
  const pending = me?.tenant && me.tenant.onboardingStep !== 'done' && can('tenant.configure');
  if (pending && location.pathname === '/app') return <Navigate to="/app/onboarding" replace />;
  return <>{children}</>;
}

export function RootRedirect() {
  const { status, me } = useAuth();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous' || !me) return <Navigate to="/login" replace />;
  return <Navigate to={me.user.type === 'platform' ? '/platform' : '/app'} replace />;
}
